import React, { ComponentType } from 'react';
import {
  Home,
  Contacts,
  Sources,
  SyncResults,
  ContactEvents,
  ContactDetail,
  Brokers,
  Stays,
  Rates,
  BookingRounds,
  Listing,
  Profile,
  Users,
  Permissions,
  Owners,
  Tenants,
  Roles,
  DriveIndex,
} from './pages';
import Invoices from "./pages/expenses/Invoices";
import InvoiceDetail from "./pages/expenses/InvoiceDetail";
import UnitDetail from "./pages/houses/UnitDetail";
import OwnerDetail from "./pages/houses/OwnerDetail";
import BrokerDetails from "./pages/partners/BrokerDetails";
import Companies from "./pages/partners/Companies";
import BookingRoundDetails from "./pages/scheduling/BookingRoundDetails";
import Listings from "./pages/houses/Listings";
import RecordTable from "./components/RecordTable";
import FormSubmissions from "./pages/contacts/FormSubmissions";
import Ambassadors from "./pages/partners/Ambassadors";
import PresentationTemplates from "./pages/marketing/Templates";
import Broadcasts from "./pages/broadcasts/Broadcasts";
import BroadcastDetail from "./pages/broadcasts/BroadcastDetail";
import { ILocation, LocationModel } from "./types/models/geo";
import {
  AmenityModel,
  UnitTypeModel,
  UnitModel,
  IAmenity,
  IUnitType,
  IUnit,
  IDevelopment, IDeveloper, DevelopmentModel, DeveloperModel,
  ICategorizedAmenity, CategorizedAmenityModel
} from "./types/models/housing";
import {
  BalanceTransactionModel,
  IBalanceTransaction,
  IInvoice,
  InvoiceModel,
  UsagePatternModel
} from "./types/models/accounting";
import { ChannelModel } from "./types/models/channels";
import {
  BookingModel,
  BookingRoundModel, CalendarNoteModel, LodgifyBookingModel,
  LodgifyPropertyModel,
  SpecialStayModel, WeekAssignmentModel,
  WeekAssignmentRoundModel
} from "./types/models/lodging";
import {
  NotificationModel,
  ListingModel,
  PromptModel,
  CompletionChatModel,
  LocaleModel,
  ScheduleModel,
  IRequestLog,
  RequestLogModel,
  NotionDatabaseModel
} from "./types/models";
import {
  ExpenseStatementModel,
  OwnerStatementModel,
  ExpenseCodeModel,
  IExpenseStatement,
  IOwnerStatement,
  IExpenseCode
} from './types/models/statements';
import StatementDetail from './pages/statements/StatementDetail';
import { SalesMaterialConfigModel, SalesMaterialSlideModel, SalesRepresentativeModel, ISalesMaterialConfig, ISalesMaterialSlide, ISalesRepresentative } from './types/models/sales';
import { CmsCategoryModel, SubcategoryModel, TagModel, PostModel, ICmsCategory, ISubcategory, ITag, IPost } from './types/models/cms';
import { SeoPageModel, ISeoMeta } from './types/models/seo';
import UnitCalendar from "./pages/scheduling/UnitCalendar";

type Route = {
  path: string;
  name: string;
  exact: boolean;
  page: ComponentType;
  icon?: ComponentType;
  forMenu: boolean;
  forDropdown?: boolean;
  groupName?: string;
  subGroupName?: string;
}

export const routes: Route[] = [
  { path: "/", name: "Home", exact: true, page: Home, forMenu: false },

  /* Website */
  // { path: "/listings2", name: "Listings2", exact: false, page: Listings2, forMenu: false },
  { path: "/listings", name: "Listings", exact: false, page: Listings, forMenu: true, groupName: 'Content' },
  // { path: "/landings", name: "Landings", exact: false, page: Listings, forMenu: true, groupName: 'Website' },
  { path: "/listings/:listing_id", name: "Listing", exact: false, page: Listing, forMenu: false },
  { path: "/listings2", name: "Listings2", exact: false, page: () => <RecordTable<any> fullpage recordModel={ListingModel} />, forMenu: false },
  { path: "/destinations", name: "Destinations", exact: false, page: () => <RecordTable<ILocation> fullpage recordModel={LocationModel} />, forMenu: true, groupName: 'Content' },
  { path: "/cms/posts", name: "Blog Posts", exact: false, page: () => <RecordTable<IPost> fullpage recordModel={PostModel} actionsWhitelist={['edit', 'Unpublish']} />, forMenu: true, groupName: 'Content' },
  // Static marketing routes (/about-us, /faqs, …) have no content record to hang SEO
  // metadata off, so they get their own table. Listings, destinations and posts are
  // edited through the SEO column on their own tables instead.
  { path: "/page-seo", name: "Page SEO", exact: false, page: () => <RecordTable<ISeoMeta> fullpage recordModel={SeoPageModel} />, forMenu: true, groupName: 'Content' },

  // { path: "/listings/:listing_id/expenses", name: "Listing Expenses", exact: false, page: Expenses, forMenu: false },
  { path: "/owners", name: "Owners", exact: false, page: Owners, forMenu: true, groupName: 'Operations' },
  { path: "/owners/:owner_id", name: "Owner", exact: false, page: OwnerDetail, forMenu: false },
  { path: "/unit-calendars", name: "Usage Calendars", exact: false, page: UnitCalendar, forMenu: true, groupName: 'Operations' },
  // { path: "/bookings", name: "House Calendars", exact: false, page: () => <RecordCalendarView recordModel={BookingModel} />, forMenu: true, groupName: 'Operations', },

  /* Expenses */
  { path: "/invoices2", name: "Invoices2", exact: false, page: Invoices, forMenu: false, groupName: 'Records', subGroupName: 'Accounting' },
  { path: "/invoices", name: "Invoices", exact: false, page: () => <RecordTable<IInvoice> allowRowSelect fullpage recordModel={InvoiceModel} />, forMenu: true, groupName: 'Records', subGroupName: 'Accounting' },
  { path: "/invoices/:invoice_id", name: "Invoice Lines", exact: false, page: InvoiceDetail, forMenu: false },
  // { path: "/payments", name: "Payments", exact: false, page: Payments, forMenu: true, groupName: 'Records', subGroupName: 'Expenses' },
  { path: "/balance-transactions", name: "Cash Balance Transactions", exact: false, page: () => <RecordTable<IBalanceTransaction> recordModel={BalanceTransactionModel} fullpage />, forMenu: true, groupName: 'Records', subGroupName: 'Accounting' },

  /* Statements */
  { path: "/statements", name: "Monthly Statements", exact: false, page: () => <RecordTable<IExpenseStatement> fullpage recordModel={ExpenseStatementModel} />, forMenu: true, groupName: 'Operations' },
  { path: "/statements/:id", name: "Statement Detail", exact: false, page: StatementDetail, forMenu: false },
  { path: "/owner-statements", name: "Owner Statements", exact: false, page: () => <RecordTable<IOwnerStatement> fullpage recordModel={OwnerStatementModel} />, forMenu: true, groupName: 'Records', subGroupName: 'Statements' },
  { path: "/expense-codes", name: "Expense Codes", exact: false, page: () => <RecordTable<IExpenseCode> fullpage recordModel={ExpenseCodeModel} />, forMenu: true, groupName: 'Records', subGroupName: 'Statements' },

  /* Sales */
  { path: "/sales-material-configs", name: "Sales Material Configs", exact: false, page: () => <RecordTable<ISalesMaterialConfig> fullpage recordModel={SalesMaterialConfigModel} />, forMenu: true, groupName: 'Records', subGroupName: 'Sales' },
  { path: "/sales-material-slides", name: "Sales Material Slides", exact: false, page: () => <RecordTable<ISalesMaterialSlide> fullpage recordModel={SalesMaterialSlideModel} />, forMenu: true, groupName: 'Records', subGroupName: 'Sales' },
  { path: "/sales-representatives", name: "Sales Representatives", exact: false, page: () => <RecordTable<ISalesRepresentative> fullpage recordModel={SalesRepresentativeModel} />, forMenu: true, groupName: 'Records', subGroupName: 'Sales' },

  /* CMS */
  { path: "/cms/categories", name: "Categories", exact: false, page: () => <RecordTable<ICmsCategory> fullpage recordModel={CmsCategoryModel} />, forMenu: true, groupName: 'Records', subGroupName: 'CMS' },
  { path: "/cms/subcategories", name: "Subcategories", exact: false, page: () => <RecordTable<ISubcategory> fullpage recordModel={SubcategoryModel} />, forMenu: true, groupName: 'Records', subGroupName: 'CMS' },
  { path: "/cms/tags", name: "Tags", exact: false, page: () => <RecordTable<ITag> fullpage recordModel={TagModel} />, forMenu: true, groupName: 'Records', subGroupName: 'CMS' },


  /* Scheduling */
  { path: "/booking-rounds", name: "Booking Rounds", exact: false, page: () => <RecordTable recordModel={BookingRoundModel} size="middle" fullpage /> , forMenu: true, groupName: 'Operations' },
  { path: "/booking-rounds/:round_id", name: "Booking Round", exact: false, page: BookingRoundDetails, forMenu: false },
  { path: "/booking-roundsss", name: "Booking Rounds", exact: false, page: BookingRounds, forMenu: false, groupName: 'Houses' },
  { path: "/special-stays", name: "Special Stays", exact: false, page: () => <RecordTable recordModel={SpecialStayModel} fullpage />, forMenu: true, groupName: 'Records', subGroupName: 'Lodging' },
  { path: "/usage-patterns", name: "Usage Patterns", exact: false, page: () => <RecordTable recordModel={UsagePatternModel} fullpage />, forMenu: true, groupName: 'Records', subGroupName: 'Lodging' },
  { path: "/lodgify-properties", name: "Lodgify Properties", exact: false, page: () => <RecordTable recordModel={LodgifyPropertyModel} fullpage />, forMenu: true, groupName: 'Records', subGroupName: 'Lodging' },
  { path: "/lodgify-bookings", name: "Lodgify Bookings", exact: false, page: () => <RecordTable recordModel={LodgifyBookingModel} fullpage />, forMenu: true, groupName: 'Records', subGroupName: 'Lodging' },
  { path: "/week-assignment-rounds", name: "WARs", exact: false, page: () => <RecordTable recordModel={WeekAssignmentRoundModel} fullpage />, forMenu: true, groupName: 'Records', subGroupName: 'Lodging' },
  { path: "/week-assignments", name: "WAs", exact: false, page: () => <RecordTable recordModel={WeekAssignmentModel} fullpage />, forMenu: true, groupName: 'Records', subGroupName: 'Lodging' },
  { path: "/calendar-notes", name: "Calendar Notes", exact: false, page: () => <RecordTable recordModel={CalendarNoteModel} fullpage />, forMenu: true, groupName: 'Records', subGroupName: 'Lodging' },
  { path: "/bookings", name: "Bookings", exact: false, page: () => <RecordTable recordModel={BookingModel} fullpage />, forMenu: true, groupName: 'Records', subGroupName: 'Lodging' },
  // { path: "/premium-dates", name: "Premium Dates", exact: false, page: PremiumDates, forMenu: true, groupName: 'Houses', subGroupName: 'Scheduling' },

  /* CRM */
  { path: "/crm/contacts", name: "Contacts", exact: false, page: Contacts, forMenu: true, groupName: 'CRM' },
  { path: "/crm/form-submissions", name: "Form Submissions", exact: false, page: FormSubmissions, forMenu: true, groupName: 'CRM' },
  { path: "/crm/contacts/:c_id", name: "Contact Detail", exact: false, page: ContactDetail, forMenu: false },
  { path: "/crm/events", name: "Contact Events", exact: false, page: ContactEvents, forMenu: true, groupName: 'CRM' },

  /* Partners */
  { path: "/brokers", name: "Brokers", exact: false, page: Brokers, forMenu: true, groupName: 'Partners' },
  { path: "/ambassadors", name: "Ambassadors", exact: false, page: Ambassadors, forMenu: true, groupName: 'Partners' },
  { path: "/brokers/:broker_id", name: "Broker Details", exact: false, page: BrokerDetails, forMenu: false },
  { path: "/companies", name: "Companies", exact: false, page: Companies, forMenu: true, groupName: 'Partners' },
  // { path: "/resources", name: "Resources", exact: false, page: Resources, forMenu: true, groupName: 'Partners' },

  /* Marketing */
  { path: "/presentation-templates", name: "Templates", exact: false, page: PresentationTemplates, forMenu: true, groupName: 'Marketing' },
  { path: "/broadcasts", name: "WhatsApp Broadcasts", exact: false, page: Broadcasts, forMenu: true, groupName: 'Marketing' },
  { path: "/broadcasts/:broadcast_id", name: "Broadcast", exact: false, page: BroadcastDetail, forMenu: false },
  // { path: "/marketing/ads", name: "Ads", exact: false, page: Ads, forMenu: true, groupName: 'Marketing' },

  /* Comms */
  { path: "/channels", name: "Channels", exact: false, page: () => <RecordTable recordModel={ChannelModel} />, forMenu: false, groupName: 'Comms' },
  { path: "/notifications", name: "Notifications", exact: false, page: () => <RecordTable allowRowSelect recordModel={NotificationModel} fullpage />, forMenu: false, groupName: 'Comms' },

  /* Misc */
  { path: "/amenities", name: "Amenities", exact: false, page: () => <RecordTable<IAmenity> fullpage recordModel={AmenityModel} />, forMenu: true, groupName: 'Records', subGroupName: 'Housing' },
  { path: "/categorized-amenities", name: "Categorized Amenities", exact: false, page: () => <RecordTable<ICategorizedAmenity> fullpage recordModel={CategorizedAmenityModel} />, forMenu: true, groupName: 'Records', subGroupName: 'Housing' },
  { path: "/units", name: "Units", exact: false, page: () => <RecordTable<IUnit> fullpage recordModel={UnitModel} />, forMenu: true, groupName: 'Records', subGroupName: 'Housing' },
  { path: "/units/:unit_id", name: "Units", exact: true, page: UnitDetail, forMenu: false },
  { path: "/designs", name: "Designs", exact: false, page: () => <RecordTable<IUnitType> fullpage recordModel={UnitTypeModel} />, forMenu: true, groupName: 'Records', subGroupName: 'Housing' },
  { path: "/developments", name: "Developments", exact: false, page: () => <RecordTable<IDevelopment> fullpage recordModel={DevelopmentModel} />, forMenu: true, groupName: 'Records', subGroupName: 'Housing' },
  // { path: "/capexes", name: "Capital Expenditures", exact: false, page: () => <RecordTable<ICapex> recordModel={CapexModel} />, forMenu: true, groupName: 'Records', subGroupName: 'Housing' },
  // { path: "/opexes", name: "Operating Expenses", exact: false, page: () => <RecordTable<IOpex> recordModel={OpexModel} />, forMenu: true, groupName: 'Records', subGroupName: 'Housing' },
  { path: "/developers", name: "Developers", exact: false, page: () => <RecordTable<IDeveloper> fullpage recordModel={DeveloperModel} />, forMenu: true, groupName: 'Records', subGroupName: 'Housing' },
  { path: "/stays", name: "Stays", exact: false, page: Stays, forMenu: true, groupName: 'Records', subGroupName: 'Lodging', },
  { path: "/rental-prices", name: "Rental Prices", exact: false, page: Rates, forMenu: true, groupName: 'Records', subGroupName: 'Lodging', },
  { path: "/prompts", name: "Prompts", exact: false, page: () => <RecordTable allowRowSelect recordModel={PromptModel} fullpage />, forMenu: true, groupName: 'Records', subGroupName: 'Misc' },
  { path: "/completion-chats", name: "Completion Chats", exact: false, page: () => <RecordTable recordModel={CompletionChatModel} fullpage />, forMenu: true, groupName: 'Records', subGroupName: 'Misc' },
  { path: "/locales", name: "Locales", exact: false, page: () => <RecordTable recordModel={LocaleModel} fullpage />, forMenu: true, groupName: 'Records', subGroupName: 'Misc' },
  { path: "/schedules", name: "Schedules", exact: false, page: () => <RecordTable recordModel={ScheduleModel} fullpage />, forMenu: true, groupName: 'Records', subGroupName: 'Misc' },
  { path: "/notion-databases", name: "Notion Databases", exact: false, page: () => <RecordTable recordModel={NotionDatabaseModel} fullpage />, forMenu: true, groupName: 'Records', subGroupName: 'Misc' },

  /* Tenancy */
  { path: "/tenants", name: "Tenants", exact: false, page: Tenants, forMenu: false, forDropdown: true },
  { path: "/roles", name: "Roles", exact: false, page: Roles, forMenu: false, forDropdown: true },

  /* Pathfinder */
  // Platform-ops surface, so it sits in the dropdown with Tenants and Request
  // Logs rather than in the main menu: the API answers 403 to everyone without
  // a platform membership, and a menu entry that 403s for most staff is noise.
  { path: "/drive-index", name: "Drive Index", exact: false, page: DriveIndex, forMenu: false, forDropdown: true },

  /* Users */
  { path: "/profile", name: "Profile", exact: false, page: Profile, forMenu: false },
  { path: "/users", name: "Users", exact: false, page: Users, forMenu: false, forDropdown: true },
  { path: "/request-logs", name: "Request Logs", exact: false, page: () => <RecordTable<IRequestLog> recordModel={RequestLogModel} fullpage allowRowSelect />, forMenu: false, forDropdown: true },
  { path: "/permissions", name: "Permissions", exact: false, page: Permissions, forMenu: false },

  /* Date Sources */
  { path: "/sources", name: "Data Pipes", exact: false, page: Sources, forMenu: true, groupName: 'Automations' },
  { path: "/sources/:source_id/syncs", name: "Data Sources Sync Results", exact: false, page: SyncResults, forMenu: false },
];
